import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, CheckCircle2, Shield, Zap, TrendingUp, HeartHandshake, ArrowRight,
  Plane, ShoppingCart, Wrench, GraduationCap, Clock, Globe, Award,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AREAS = [
  {
    icon: Plane,
    title: 'Vendita Velivolo',
    desc: 'Analisi di mercato, determinazione del valore reale, ottimizzazione annuncio e supporto completo alla trattativa.',
    bullets: ['Valutazione tecnica ed economica', 'Ottimizzazione listing', 'Supporto alla trattativa'],
  },
  {
    icon: ShoppingCart,
    title: 'Acquisto Velivolo',
    desc: 'Guida nella scelta, controlli tecnici, verifica documentazione e certificazioni per un acquisto sicuro.',
    bullets: ['Screening del velivolo', 'Pre-purchase inspection', 'Verifica documentale'],
  },
  {
    icon: Wrench,
    title: 'Gestione & Manutenzione',
    desc: 'Assistenza nella manutenzione, gestione dei logbook e ottimizzazione dei costi operativi.',
    bullets: ['Logbook & compliance', 'Programmi di manutenzione', 'Ottimizzazione costi'],
  },
  {
    icon: GraduationCap,
    title: 'Brevetti & Formazione',
    desc: 'Orientamento completo sui brevetti, pianificazione dei corsi e accesso ai simulatori certificati.',
    bullets: ['PPL, CPL, ATPL', 'Type rating', 'Simulatori certificati'],
  },
];

const STEPS = [
  { n: '01', title: 'Richiesta', desc: 'Compila il form indicando tipo di consulenza ed esigenze specifiche.' },
  { n: '02', title: 'Call Iniziale', desc: 'Un consulente ti contatta entro 24h per comprendere obiettivi e vincoli.' },
  { n: '03', title: 'Analisi', desc: 'Studiamo il mercato, il velivolo o il percorso formativo più adatto a te.' },
  { n: '04', title: 'Piano d\'Azione', desc: 'Ricevi una strategia concreta e il supporto operativo fino all\'obiettivo.' },
];

const BENEFITS = [
  { icon: Users, title: 'Consulenza su misura', desc: 'Supporto personalizzato per vendita, acquisto, gestione o brevetti.' },
  { icon: Shield, title: 'Esperti del settore', desc: 'Piloti, tecnici e consulenti certificati al tuo fianco.' },
  { icon: Zap, title: 'Processo rapido', desc: 'Dalla prima call al piano d\'azione in modo chiaro ed efficiente.' },
  { icon: TrendingUp, title: 'Massimizza il valore', desc: 'Strategie per trattative, annunci e gestione del velivolo.' },
  { icon: HeartHandshake, title: 'Supporto continuo', desc: 'Dalla manutenzione ai brevetti, seguiamo ogni fase.' },
  { icon: Award, title: 'Trasparenza totale', desc: 'Tariffe chiare, nessuna commissione nascosta.' },
];

const OGGETTI = [
  'Vendita velivolo',
  'Acquisto velivolo',
  'Gestione e manutenzione',
  'Brevetti e formazione',
  'Altro',
];

export default function Consulenze() {
  const [form, setForm] = useState({ nome: '', cognome: '', email: '', oggetto: '', messaggio: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = 'Consulenze per Vendita, Acquisto e Gestione di Velivoli | AeroDeals';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'Consulenze professionali AeroDeals: supporto su misura per vendita, acquisto, gestione di velivoli, brevetti e formazione piloti. Decidi con sicurezza e ottimizza ogni investimento.');
  }, []);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const scrollToForm = (preselect) => {
    if (preselect) setForm((s) => ({ ...s, oggetto: preselect }));
    document.getElementById('form-consulenza')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.cognome || !form.email || !form.oggetto) {
      toast.error('Compila tutti i campi obbligatori.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/consulenze/request`, form);
      setSent(true);
      toast.success('Richiesta inviata. Ti contatteremo a breve.');
      setForm({ nome: '', cognome: '', email: '', oggetto: '', messaggio: '' });
    } catch (err) {
      console.error(err);
      toast.error("Errore durante l'invio. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] pt-20" data-testid="consulenze-page">
      {/* ─── Hero ─── */}
      <section className="relative py-24 md:py-32 border-b border-zinc-800/50 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#C5A059]/[0.08] via-transparent to-transparent pointer-events-none" />
        <div className="absolute right-[-10%] top-[-10%] w-[600px] h-[600px] rounded-full bg-[#C5A059]/[0.04] blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.2fr,1fr] gap-12 items-center">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-4">Servizi · Consulenze</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-white mb-6 leading-[1.05]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Consulenze<br />Professionali<br /><span className="text-[#C5A059]">su Misura</span>
              </h1>
              <p className="text-base md:text-lg text-zinc-300 leading-relaxed mb-8 max-w-xl">
                Supporto per vendita, acquisto, gestione di velivoli e formazione piloti. Decidi con sicurezza ed
                ottimizza ogni investimento.
              </p>
              <div className="flex flex-wrap gap-3 mb-10">
                <button
                  onClick={() => scrollToForm()}
                  className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-6 py-3 font-medium text-sm hover:bg-[#D4B271] transition-colors"
                  data-testid="cta-hero-consulenza"
                >
                  Richiedi la tua Consulenza <ArrowRight size={14} />
                </button>
                <a
                  href="#aree"
                  className="inline-flex items-center gap-2 border border-zinc-700 text-zinc-300 px-6 py-3 font-medium text-sm hover:border-[#C5A059] hover:text-white transition-colors"
                >
                  Scopri le aree
                </a>
              </div>
              {/* Stats bar */}
              <div className="grid grid-cols-3 gap-4 max-w-lg border-t border-zinc-800 pt-6">
                <div className="flex items-start gap-2.5">
                  <Clock size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white text-sm font-medium">&lt; 24h</div>
                    <div className="text-[11px] text-zinc-500 leading-tight">Tempo di risposta</div>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Globe size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white text-sm font-medium">Italia & EU</div>
                    <div className="text-[11px] text-zinc-500 leading-tight">Copertura operativa</div>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Award size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white text-sm font-medium">Certificati</div>
                    <div className="text-[11px] text-zinc-500 leading-tight">Piloti e tecnici</div>
                  </div>
                </div>
              </div>
            </div>
            {/* Hero visual card */}
            <div className="hidden lg:block">
              <div className="relative border border-zinc-800 bg-gradient-to-br from-zinc-900 to-[#0E0E10] p-8">
                <div className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#C5A059]/40 to-transparent" />
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#C5A059]/15">
                    <Users size={18} className="text-[#C5A059]" />
                  </div>
                  <div>
                    <div className="text-xs font-mono uppercase tracking-widest text-[#C5A059]">AeroDeals Advisory</div>
                    <div className="text-sm text-zinc-400">Team dedicato</div>
                  </div>
                </div>
                <ul className="space-y-3.5">
                  {['Valutazione tecnica ed economica', 'Supporto alla trattativa', 'Gestione documentale completa', 'Orientamento formativo'].map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm text-zinc-300">
                      <CheckCircle2 size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-6 border-t border-zinc-800 text-xs text-zinc-500">
                  Adatto a piloti sportivi, piloti privati, operatori e professionisti certificati.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Aree di Consulenza ─── */}
      <section id="aree" className="py-20 md:py-24 border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-xl">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Aree di Consulenza</p>
              <h2 className="text-3xl md:text-4xl tracking-tighter font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Quattro ambiti, un unico team di esperti
              </h2>
            </div>
            <p className="text-sm text-zinc-400 md:text-right max-w-sm">
              Seleziona l&apos;area che ti interessa e richiedi una consulenza dedicata.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {AREAS.map((a) => (
              <button
                key={a.title}
                onClick={() => scrollToForm(a.title.replace(' Velivolo', ' velivolo').replace('Gestione & Manutenzione', 'Gestione e manutenzione').replace('Brevetti & Formazione', 'Brevetti e formazione'))}
                className="group text-left bg-zinc-900/40 border border-zinc-800 p-7 hover:border-[#C5A059]/50 hover:bg-zinc-900/70 transition-all"
                data-testid={`area-${a.title.toLowerCase().replace(/\s|&/g, '-')}`}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#C5A059]/15 group-hover:bg-[#C5A059]/25 transition-colors">
                    <a.icon size={22} className="text-[#C5A059]" />
                  </div>
                  <ArrowRight size={16} className="text-zinc-600 group-hover:text-[#C5A059] group-hover:translate-x-1 transition-all" />
                </div>
                <h3 className="text-xl font-medium text-white mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {a.title}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed mb-5">{a.desc}</p>
                <ul className="space-y-2">
                  {a.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2 text-xs text-zinc-500">
                      <span className="w-1 h-1 bg-[#C5A059] rounded-full" />
                      {b}
                    </li>
                  ))}
                </ul>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Intro narrativa ─── */}
      <section className="py-20 md:py-24 border-b border-zinc-800/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.8fr,1.2fr] gap-10 lg:gap-16">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Il servizio</p>
              <h2 className="text-2xl md:text-3xl tracking-tighter font-medium text-white leading-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Consulenze per Vendita, Acquisto e Gestione di Velivoli
              </h2>
            </div>
            <div className="space-y-5 text-zinc-300 leading-relaxed text-[15px]">
              <p>
                AeroDeals offre consulenze su misura per chi desidera acquistare, vendere o gestire velivoli privati e
                commerciali, oppure necessita di supporto per brevetti e formazione. I nostri esperti ti guideranno passo
                passo, fornendoti assistenza di tipo tecnico, finanziario ed aspetto legale per prendere decisioni sicure,
                ottimizzare gli investimenti e ridurre rischi.
              </p>
              <p>
                Analizziamo ogni velivolo per determinarne il valore reale, ottimizzare la presentazione negli annunci e
                supportare le trattative, garantendo transazioni trasparenti ed efficienti. Offriamo consulenza anche nella
                scelta del velivolo più adatto alle tue esigenze, con controlli tecnici accurati e verifica di
                documentazione e certificazioni.
              </p>
              <p>
                Per chi già possiede un velivolo, assistiamo nella manutenzione, nella gestione dei logbook e dei costi
                operativi, con consigli strategici per ottimizzare la gestione complessiva. Chi desidera ottenere o
                aggiornare brevetti riceve orientamento completo sulla formazione necessaria, pianificazione dei corsi e
                accesso ai simulatori, con indicazioni normative e tecniche precise.
              </p>
              <p>
                Ogni consulenza è progettata per offrire sicurezza, efficienza e il massimo valore, adattandosi alle
                esigenze di piloti sportivi, piloti privati, operatori e professionisti certificati.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Come Funziona ─── */}
      <section className="py-20 md:py-24 border-b border-zinc-800/50 bg-zinc-950/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Come Funziona</p>
          <h2 className="text-3xl md:text-4xl tracking-tighter font-medium text-white mb-12 max-w-2xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Dalla richiesta al risultato in 4 passaggi
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <div key={s.n} className="relative bg-zinc-900/40 border border-zinc-800 p-6 hover:border-[#C5A059]/40 transition-colors" data-testid={`step-${s.n}`}>
                <div className="text-5xl font-medium text-[#C5A059]/30 mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{s.n}</div>
                <h3 className="text-lg font-medium text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{s.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
                {i < STEPS.length - 1 && (
                  <ArrowRight size={14} className="hidden lg:block absolute top-8 -right-3 text-zinc-700" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Benefits ─── */}
      <section className="py-20 md:py-24 border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Perché Sceglierci</p>
          <h2 className="text-3xl md:text-4xl tracking-tighter font-medium text-white mb-12 max-w-2xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Un servizio costruito attorno alle tue esigenze
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b, i) => (
              <div key={b.title} className="flex gap-4 bg-zinc-900/40 border border-zinc-800 p-6 hover:border-[#C5A059]/40 transition-colors" data-testid={`benefit-${i}`}>
                <div className="w-11 h-11 flex items-center justify-center bg-[#C5A059]/15 shrink-0">
                  <b.icon size={20} className="text-[#C5A059]" />
                </div>
                <div>
                  <h3 className="text-base font-medium text-white mb-1.5" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{b.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Form ─── */}
      <section id="form-consulenza" className="py-20 md:py-24 border-b border-zinc-800/50 bg-gradient-to-b from-transparent to-[#C5A059]/[0.03]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.9fr,1.3fr] gap-10 lg:gap-14 items-start">
            <div className="lg:sticky lg:top-28">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Richiedi una Consulenza</p>
              <h2 className="text-3xl md:text-4xl tracking-tighter font-medium text-white mb-5" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Richiedere una consulenza è semplice
              </h2>
              <p className="text-sm text-zinc-400 leading-relaxed mb-8">
                Seleziona il tipo di consulenza, compila il form con i tuoi dati e le tue esigenze specifiche: un nostro
                consulente ti contatterà rapidamente per aiutarti a ottimizzare l&apos;acquisto, la vendita o la gestione
                del tuo velivolo.
              </p>
              <ul className="space-y-3 text-sm text-zinc-300">
                {['Risposta entro 24 ore', 'Nessun impegno iniziale', 'Privacy e riservatezza garantite'].map((t) => (
                  <li key={t} className="flex items-center gap-2.5">
                    <CheckCircle2 size={15} className="text-[#C5A059] shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              {sent ? (
                <div className="border border-[#C5A059]/40 bg-[#C5A059]/[0.06] p-10 text-center" data-testid="consulenza-success">
                  <CheckCircle2 size={42} className="text-[#C5A059] mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    Richiesta inviata con successo
                  </h3>
                  <p className="text-sm text-zinc-400 mb-6">Grazie! Un nostro consulente ti contatterà al più presto.</p>
                  <button
                    onClick={() => setSent(false)}
                    className="text-xs font-mono uppercase tracking-widest text-[#C5A059] hover:text-[#D4B271] transition-colors"
                  >
                    Invia un&apos;altra richiesta
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="bg-zinc-900/60 border border-zinc-800 p-7 md:p-9 space-y-5" data-testid="consulenza-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Nome *</label>
                      <input type="text" value={form.nome} onChange={update('nome')} required className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C5A059] transition-colors" data-testid="input-nome" />
                    </div>
                    <div>
                      <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Cognome *</label>
                      <input type="text" value={form.cognome} onChange={update('cognome')} required className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C5A059] transition-colors" data-testid="input-cognome" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Email *</label>
                    <input type="email" value={form.email} onChange={update('email')} required className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C5A059] transition-colors" data-testid="input-email" />
                  </div>

                  <div>
                    <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Oggetto *</label>
                    <select value={form.oggetto} onChange={update('oggetto')} required className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C5A059] transition-colors cursor-pointer" data-testid="input-oggetto">
                      <option value="">Seleziona il tipo di consulenza...</option>
                      {OGGETTI.map((o) => (
                        <option key={o} value={o} className="bg-zinc-950">{o}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Messaggio</label>
                    <textarea rows={5} value={form.messaggio} onChange={update('messaggio')} placeholder="Raccontaci le tue esigenze (opzionale)" className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C5A059] transition-colors resize-none" data-testid="input-messaggio" />
                  </div>

                  <button type="submit" disabled={loading} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#C5A059] text-black px-8 py-3 font-medium text-sm hover:bg-[#D4B271] transition-colors disabled:opacity-60 disabled:cursor-not-allowed" data-testid="submit-consulenza">
                    {loading ? 'Invio in corso...' : 'Richiedi Consulenza'}
                    {!loading && <ArrowRight size={14} />}
                  </button>

                  <p className="text-xs text-zinc-500 leading-relaxed">
                    Inviando la richiesta accetti la nostra <a href="/privacy" className="text-[#C5A059] hover:underline">Privacy Policy</a>.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Outro ─── */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <blockquote className="border-l-2 border-[#C5A059] pl-6 text-zinc-300 leading-relaxed">
            <strong className="text-white">Scopri tutti i velivoli attualmente disponibili</strong> visitando la nostra pagina{' '}
            <a href="/listings" className="text-[#C5A059] hover:underline">Annunci</a>. Trova facilmente il velivolo
            più adatto alle tue esigenze, messaggia con il venditore e concludi l&apos;affare con AeroDeals.
          </blockquote>
        </div>
      </section>
    </div>
  );
}
